import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowIcon } from "assets/svgs/Arrow.svg";
import { ReactComponent as ImageIcon } from "assets/svgs/Image.svg";
import { ReactComponent as StairIcon } from "assets/svgs/Stair.svg";
import { ReactComponent as ClockIcon } from "assets/svgs/Clock.svg";
import { ReactComponent as DockingIcon } from "assets/svgs/docking.svg";
import { ReactComponent as BinIcon } from "assets/svgs/Editor/bin.svg";
import { ReactComponent as PencilIcon } from "assets/svgs/Pencil.svg";
import { ReactComponent as LocationIcon } from "assets/svgs/navigation/Location.svg";
import { ReactComponent as WarningIcon } from "assets/svgs/toast/Warning.svg";
import { ReactComponent as ControlArmIcon } from "assets/svgs/navbar/robot-arm-svgrepo-com.svg";
import { Action } from "app/services/types.generated";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import { formatMissionFeedbackForDisplay } from "lib/utils/formatMissionLogDisplay";
import {
  MISSION_END_DOCK_NOTE,
  PREPENDED_UNDOCK_BEFORE_LOAD_MAP_NOTE,
} from "lib/mission/dockingMissionSteps";
import { MISSION_END_STOP_360_NOTE } from "lib/mission/record360MissionSteps";
import {
  fetchMissionStorageMapPickerGroups,
  type MissionStorageLoadMapPickerRow,
} from "lib/mission/missionStorageMaps";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { formatWaitDuration } from "./WaitDialog";

interface Pose {
  position: {
    x: number;
    y: number;
    z: number;
  },
  orientation: {
    x: number;
    y: number;
    z: number;
    w: number
  }
}

interface NavigateToArgs {
  pose: Pose;
  map_resource_id: string;
}

interface TakePhotoArgs {
  camera_name: string;
  ptz_yaw?: number;
  ptz_pitch?: number;
}

interface Record360Args {
  command?: string;
  duration_sec?: number;
  download?: boolean;
}

interface WalkStairArgs {
  direction: string;
  landing?: number;
  levels: number;
  correction_mode?: string;
}

interface WaitActionArgs {
  duration: number;
}

interface PressElevatorButtonArgs {
  button_description: string;
  approach_offset_m?: number;
  press_depth_m?: number;
  move_duration_s?: number;
}

interface ArmPresetArgs {
  command: "go_zero" | "ready" | "stow" | string;
}

interface DockingArgs {
  standoff_distance?: number;
  target_range_m?: number;
  show_viewer?: boolean;
}

interface LoadMapArgs {
  map_id?: string;
  variant_bmp_rel_path?: string;
  map_file_id?: string;
}

interface SaveMapArgs {
  map_name?: string;
}

interface StepStatus {
  action_id: string;
  action_type: string;
  action_name: string;
  status: string; // "pending", "running", "completed", "failed", "skipped"
  sub_feedback: string; // JSON serialized feedback (live, while running)
  sub_feedback_type: string;
  sub_result: string; // JSON serialized result
  sub_result_type: string;
  error: string;
}

export type LoadMapFilterSelection = {
  map_id: string;
  variant_bmp_rel_path?: string;
  map_file_id?: string;
};

interface ActionRowProps {
  action: Action;
  /** 1-based NavigateTo index in mission order (map pin number); omit for non-navigate steps. */
  navigateToOrdinal?: number;
  onNoteChange?: (actionId: string, newNote: string) => void;
  onSaveMapAutoLoadToggle?: (actionId: string, enabled: boolean) => void;
  onLoadMapFilterChange?: (actionId: string, next: LoadMapFilterSelection) => void;
  /** ARC host for fetching keep-out filter variants (LoadMap). */
  arcHost?: string | null;
  showRemoveButton?: boolean;
  readOnly?: boolean;
  /** Edit chrome is on: hide run/step status so stale statuses from a finished
   * or cancelled run cannot block remove / map-filter controls. */
  editing?: boolean;
  onRemove?: (actionId: string) => void;
  startHereMode?: boolean;
  onStartHereSelect?: () => void;
}

/** Last path segment for UI (absolute storage paths or relative map paths). */
const pathLeafName = (raw: string): string => {
  const norm = raw.replace(/\\/g, "/").trim();
  if (!norm) return "";
  const noQuery = norm.split(/[?#]/)[0] ?? norm;
  const parts = noQuery.split("/").filter(Boolean);
  return parts[parts.length - 1] || norm;
};

const getActionIcon = (actionType: string) => {
  switch (actionType) {
    case 'NavigateTo':
      return <ArrowIcon className="w-6 h-6" />;
    case 'TakePhoto':
      return <ImageIcon className="w-6 h-6" />;
    case 'Record360':
      return <ImageIcon className="w-6 h-6" />;
    case 'WalkStair':
      return <StairIcon className="w-6 h-6" />;
    case 'Docking':
    case 'Dock':
      return <DockingIcon className="w-6 h-6" />;
    case 'Undock':
      return <DockingIcon className="w-6 h-6 rotate-180" />;
    case 'Wait':
      return <ClockIcon className="w-6 h-6" />;
    case 'LoadMap':
      return <ArrowIcon className="w-6 h-6 rotate-180" />;
    case 'SaveMap':
      return <ArrowIcon className="w-6 h-6 -rotate-90" />;
    case 'Relocalization':
      return <LocationIcon className="w-6 h-6" />;
    case 'PressElevatorButton':
    case 'ArmPreset':
      return <ControlArmIcon className="w-6 h-6" />;
    case 'Failure':
      return <WarningIcon className="w-6 h-6 text-warning" />;
    default:
      return <ArrowIcon className="w-6 h-6" />;
  }
};

const getActionDescription = (action: Action): string => {
  switch (action.actionType) {
    case 'NavigateTo': {
      const args = action.args as unknown as NavigateToArgs;
      return `Navigate to (${args.pose.position.x.toFixed(2)}, ${args.pose.position.y.toFixed(2)})`;
    }
    case 'TakePhoto': {
      const args = action.args as unknown as TakePhotoArgs;
      const yaw = Number(args.ptz_yaw ?? 0);
      const pitch = Number(args.ptz_pitch ?? 0);
      const ptz = (yaw !== 0 || pitch !== 0)
        ? ` · PTZ ${yaw.toFixed(1)}°/${pitch.toFixed(1)}°`
        : '';
      return `Take photo — ${args.camera_name ?? 'gimbal'}${ptz}`;
    }
    case 'Record360': {
      const args = action.args as unknown as Record360Args;
      const command = String(args.command || "").toLowerCase();
      const download = args.download !== false;
      if (command === "start") {
        return "Start 360 recording";
      }
      if (command === "stop") {
        if (action.note === MISSION_END_STOP_360_NOTE) {
          return `Stop 360 recording at mission end${download ? " · download" : ""}`;
        }
        return `Stop 360 recording${download ? " · download" : ""}`;
      }
      const seconds = Number(args.duration_sec ?? 30);
      return `Record 360 — ${seconds}s${download ? " · download" : ""}`;
    }
    case 'WalkStair': {
      const args = action.args as unknown as WalkStairArgs;
      const landing = args.landing ?? 0;
      const landingPart = landing > 0 ? `, ${landing} landing(s)` : '';
      const mode = args.correction_mode ?? "vision";
      return `Navigate ${args.direction} ${args.levels} level(s)${landingPart} (${mode})`;
    }
    case 'Docking':
    case 'Dock':
    case 'Undock': {
      if (action.note === PREPENDED_UNDOCK_BEFORE_LOAD_MAP_NOTE) {
        return 'Undock before load map';
      }
      if (action.note === MISSION_END_DOCK_NOTE) {
        return 'Dock at mission end';
      }
      const args = action.args as unknown as DockingArgs;
      const standoff = args.standoff_distance ?? 0;
      const targetRange = args.target_range_m ?? 0;
      const parts: string[] = [];
      if (standoff > 0) {
        parts.push(`standoff ${standoff.toFixed(2)} m`);
      }
      if (targetRange > 0) {
        parts.push(`undock range ${targetRange.toFixed(2)} m`);
      }
      return parts.length > 0 ? parts.join(' · ') : 'Docking';
    }
    case 'Wait': {
      const args = action.args as unknown as WaitActionArgs;
      return `Wait for ${formatWaitDuration(args.duration)}`;
    }
    case 'LoadMap': {
      const args = action.args as unknown as LoadMapArgs;
      if (args.variant_bmp_rel_path) {
        const variantLabel = pathLeafName(args.variant_bmp_rel_path);
        return variantLabel
          ? `Keep-out filter: ${variantLabel}`
          : "Keep-out filter applied";
      }
      return args.map_id ? "No map filter" : "Load map from storage";
    }
    case 'SaveMap': {
      const args = action.args as unknown as SaveMapArgs;
      return args.map_name ? `Save current map as "${args.map_name}"` : 'Save current map';
    }
    case 'Relocalization':
      return 'Relocalize on current map';
    case 'PressElevatorButton': {
      const args = action.args as unknown as PressElevatorButtonArgs;
      const label = String(args.button_description || "").trim() || "?";
      return `Press elevator button "${label}"`;
    }
    case 'ArmPreset': {
      const args = action.args as unknown as ArmPresetArgs;
      const command = String(args.command || "").trim();
      if (command === "go_zero") return "Arm go to zero";
      if (command === "ready") return "Arm ready pose";
      if (command === "stow") return "Arm stow pose";
      return command ? `Arm preset: ${command}` : "Arm preset";
    }
    case 'Failure':
      return 'Fails immediately (test only)';
    default:
      return '';
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'running':
      return 'bg-yellow-500';
    case 'paused':
      return 'bg-amber-600';
    case 'completed':
      return 'bg-green-500';
    case 'failed':
      return 'bg-red-500';
    case 'skipped':
      return 'bg-gray-500';
    case 'pending':
    default:
      return 'bg-black60';
  }
};

const getStatusText = (status: string): string => {
  switch (status) {
    case 'running':
      return 'Running';
    case 'paused':
      return 'Paused';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
};

const ActionRowView: React.FC<ActionRowProps & { stepStatus?: StepStatus }> = ({
  action,
  navigateToOrdinal,
  onNoteChange,
  onSaveMapAutoLoadToggle,
  onLoadMapFilterChange,
  arcHost = null,
  showRemoveButton = false,
  readOnly = false,
  onRemove,
  startHereMode = false,
  onStartHereSelect,
  stepStatus,
}) => {
  const { t } = useTranslation("missions");
  const isSecure = useIsHttpSecure();
  const arcId = useFleetArcId();
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState(action.note);
  const [showResult, setShowResult] = useState(false);
  const [loadMapVariants, setLoadMapVariants] = useState<MissionStorageLoadMapPickerRow[]>([]);
  const [loadMapVariantsLoading, setLoadMapVariantsLoading] = useState(false);

  const handleNoteBlur = () => {
    setIsEditingNote(false);
    onNoteChange?.(action.id, noteValue);
  };

  const actionTitle =
    action.actionType === "NavigateTo" && navigateToOrdinal != null
      ? `${navigateToOrdinal} ${action.name}`
      : action.name;

  const isRunning = stepStatus?.status === 'running';
  const hasResult = Boolean(
    stepStatus?.sub_result &&
      stepStatus.sub_result !== '{}' &&
      stepStatus.status !== 'running' &&
      stepStatus.status !== 'paused',
  );
  const hasError = stepStatus?.status === 'failed';
  const hasFeedback = Boolean(isRunning && stepStatus?.sub_feedback && stepStatus.sub_feedback !== '' && stepStatus.sub_feedback !== '{}');

  const isSaveMapAction = action.actionType === 'SaveMap';
  const saveMapArgs = (action.args ?? {}) as unknown as { mission_id?: string };
  const autoLoadEnabled = Boolean(saveMapArgs.mission_id);

  const isLoadMapAction = action.actionType === 'LoadMap';
  const loadMapArgs = (action.args ?? {}) as unknown as LoadMapArgs;
  const loadMapId = (loadMapArgs.map_id || "").trim();
  const selectedFilterPath = (loadMapArgs.variant_bmp_rel_path || "").trim();
  const showLoadMapFilter =
    !readOnly && !stepStatus && isLoadMapAction && Boolean(onLoadMapFilterChange) && Boolean(loadMapId);

  useEffect(() => {
    if (!showLoadMapFilter || !loadMapId) {
      setLoadMapVariants([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadMapVariantsLoading(true);
      try {
        const groups = await fetchMissionStorageMapPickerGroups(arcHost, isSecure, arcId);
        if (cancelled) return;
        const group = groups.find((g) => g.mapId === loadMapId);
        setLoadMapVariants(group?.variants ?? []);
      } catch {
        if (!cancelled) setLoadMapVariants([]);
      } finally {
        if (!cancelled) setLoadMapVariantsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [showLoadMapFilter, loadMapId, arcHost, isSecure, arcId]);

  const handleLoadMapFilterSelect = (value: string) => {
    if (!onLoadMapFilterChange || !loadMapId) return;
    if (!value) {
      onLoadMapFilterChange(action.id, { map_id: loadMapId });
      return;
    }
    const variant = loadMapVariants.find(
      (v) => (v.variantBmpRelPath || "").trim() === value,
    );
    onLoadMapFilterChange(action.id, {
      map_id: loadMapId,
      ...(value ? { variant_bmp_rel_path: value } : {}),
      ...(variant?.mapFileId ? { map_file_id: variant.mapFileId } : {}),
    });
  };

  const isStartHereSelectable = startHereMode && action.actionType === "NavigateTo";
  const startHereMuted = startHereMode && !isStartHereSelectable;

  return (
    <div
      className={`flex w-full space-x-3 border-b border-black80 p-2 transition-colors ${
        isStartHereSelectable
          ? "cursor-pointer rounded-r5 hover:bg-black70 hover:ring-1 hover:ring-primary2"
          : ""
      } ${startHereMuted ? "pointer-events-none opacity-40" : ""}`}
      role={isStartHereSelectable ? "button" : undefined}
      tabIndex={isStartHereSelectable ? 0 : undefined}
      onClick={
        isStartHereSelectable
          ? () => onStartHereSelect?.()
          : undefined
      }
      onKeyDown={
        isStartHereSelectable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onStartHereSelect?.();
              }
            }
          : undefined
      }
    >
      <div className="flex-shrink-0 mt-1">
        {getActionIcon(action.actionType)}
      </div>
      <div className="flex min-w-0 flex-1 flex-col space-y-2">
        <div className="flex min-w-0 items-center justify-between gap-3">
          <p
            className="min-w-0 shrink truncate text-h5 font-semibold"
            title={actionTitle}
          >
            {action.actionType === "NavigateTo" && navigateToOrdinal != null ? (
              <>
                <span className="tabular-nums text-black40">{navigateToOrdinal}</span>
                {" "}
                {action.name}
              </>
            ) : (
              action.name
            )}
          </p>
          {!stepStatus && !readOnly && !isEditingNote && (
            <button
              type="button"
              title={noteValue ? "Edit note" : "Add note"}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-r3 text-black60 transition-colors hover:bg-black70 hover:text-white"
              onClick={() => setIsEditingNote(true)}
            >
              <PencilIcon className="w-4" />
            </button>
          )}
          {/* Spacer keeps the pen next to the name and the bin at the far right */}
          <div className="min-w-2 flex-1" aria-hidden />
          <div className="flex shrink-0 items-center gap-2">
            {showRemoveButton && !readOnly && !stepStatus && (
              <button
                type="button"
                className="rounded-r5 border border-warning p-1 text-warning transition-colors hover:bg-warning hover:text-black100"
                title="Remove action"
                onClick={() => onRemove?.(action.id)}
              >
                <BinIcon className="h-5 w-5" />
              </button>
            )}
            {stepStatus && (
              <div className="flex shrink-0 items-center gap-2">
                {isRunning ? (
                  <div className="w-4 h-4 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin" />
                ) : (
                  <div className={`h-2 w-2 shrink-0 rounded-full ${getStatusColor(stepStatus.status)}`} />
                )}
                <span className="whitespace-nowrap text-h5 text-black40">
                  {getStatusText(stepStatus.status)}
                </span>
              </div>
            )}
          </div>
        </div>
        <p className="break-words text-h5 text-black40">{getActionDescription(action)}</p>

        {/* SaveMap behavior toggle */}
        {!readOnly && !stepStatus && isSaveMapAction && onSaveMapAutoLoadToggle && (
          <div className="flex items-center justify-between rounded-r5 border border-black80 bg-black70 p-2">
            <div>
              <p className="text-h5 font-semibold">Auto-add LoadMap action</p>
              <p className="text-h5 text-black40">
                When this SaveMap runs, append a LoadMap step using the saved map.
              </p>
            </div>
            <button
              type="button"
              className={`h-8 min-w-[66px] rounded-r5 px-3 text-h5 transition-colors ${
                autoLoadEnabled ? "bg-primary2 text-white" : "bg-black80 text-black40"
              }`}
              onClick={() => onSaveMapAutoLoadToggle(action.id, !autoLoadEnabled)}
            >
              {autoLoadEnabled ? "ON" : "OFF"}
            </button>
          </div>
        )}

        {/* LoadMap keep-out filter selector */}
        {showLoadMapFilter && (
          <div className="flex flex-col gap-1 rounded-r5 border border-black80 bg-black70 p-2">
            <label
              htmlFor={`loadmap-filter-${action.id}`}
              className="text-h5 font-semibold"
            >
              {t("actions.keepOutFilter")}
            </label>
            <p className="text-h5 text-black40">
              {loadMapVariantsLoading
                ? t("dialogs.loadingMaps")
                : t("actions.keepOutFilterHint")}
            </p>
            <select
              id={`loadmap-filter-${action.id}`}
              className="mt-1 w-full rounded-r5 border border-black80 bg-black80 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none"
              value={selectedFilterPath}
              disabled={loadMapVariantsLoading}
              onChange={(e) => handleLoadMapFilterSelect(e.target.value)}
            >
              <option value="">{t("actions.noMapFilter")}</option>
              {selectedFilterPath &&
                !loadMapVariants.some(
                  (v) => (v.variantBmpRelPath || "").trim() === selectedFilterPath,
                ) && (
                  <option value={selectedFilterPath}>
                    {pathLeafName(selectedFilterPath) || selectedFilterPath}
                  </option>
                )}
              {loadMapVariants.map((variant) => {
                const path = (variant.variantBmpRelPath || "").trim();
                if (!path) return null;
                return (
                  <option key={variant.rowId} value={path}>
                    {variant.label || pathLeafName(path) || path}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {/* Live feedback while running */}
        {hasFeedback && stepStatus && (
          <div className="p-2 bg-black80 rounded">
            <pre className="text-h5 text-black40 whitespace-pre-wrap break-words">
              {formatMissionFeedbackForDisplay(stepStatus.sub_feedback)}
            </pre>
          </div>
        )}

        {/* Error display */}
        {hasError && stepStatus && (
          <div className="p-2 bg-red-900 rounded">
            <p className="text-h5 text-red-300">Error: {stepStatus.error}</p>
          </div>
        )}

        {/* Result toggle button and display */}
        {hasResult && stepStatus && (
          <div className="flex flex-col">
            <button
              onClick={() => setShowResult(!showResult)}
              className="text-h5 text-primary2 hover:text-primary1 text-left"
            >
              {showResult ? '▼ Hide result' : '▶ Show result'}
            </button>
            {showResult && (
              <div className="mt-2 p-2 bg-black80 rounded overflow-auto max-h-40">
                <pre className="text-h5 text-black40 whitespace-pre-wrap break-words">
                  {stepStatus.sub_result}
                </pre>
                {stepStatus.sub_result_type && (
                  <p className="text-h5 text-black60 mt-1">
                    Type: {stepStatus.sub_result_type}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Note section - only show when not executing */}
        {!stepStatus && readOnly && noteValue && (
          <p className="text-h5 text-black40 p-2">{noteValue}</p>
        )}
        {!stepStatus && !readOnly && (
          isEditingNote ? (
            <div className="flex flex-col">
              <textarea
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value.slice(0, 1000))}
                onBlur={handleNoteBlur}
                autoFocus
                maxLength={1000}
                className="w-full bg-black70 border border-primary2 rounded p-2 text-h5 resize-none focus:outline-none"
                rows={3}
              />
              <p className="text-h5 text-black60 text-right mt-1">
                {noteValue.length}/1000
              </p>
            </div>
          ) : (
            noteValue && (
              <button
                type="button"
                title="Edit note"
                className="break-words text-left text-h5 text-black40 transition-colors hover:text-white"
                onClick={() => setIsEditingNote(true)}
              >
                {noteValue}
              </button>
            )
          )
        )}

        {/* Show note read-only when executing */}
        {stepStatus && noteValue && (
          <p className="text-h5 text-black40 p-2">{noteValue}</p>
        )}
      </div>
    </div>
  );
};

const LiveActionRow: React.FC<ActionRowProps> = (props) => {
  const { stepStatuses } = useMissionExecution();
  // Ignore stale execution statuses while editing a mission.
  return <ActionRowView {...props} stepStatus={props.editing ? undefined : stepStatuses[props.action.id]} />;
};

const ActionRow: React.FC<ActionRowProps> = (props) =>
  props.readOnly ? <ActionRowView {...props} /> : <LiveActionRow {...props} />;

export default ActionRow;
